import React, { useState } from "react";
import { useDebounce } from "react-use";
import shortid from "shortid";
import styled from "styled-components";

import {
  coursePlanner,
  instructionDatesBetween,
  toAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  Breadcrumbs,
  useQueryGetCourse,
  useQueryGetCourseUnits,
  useQueryGetCoursesBySubject,
  useQueryListAcademicCalendars,
  useAppContext,
} from "@examspark/common-app";
import { subjectMeta } from "@examspark/common-meta";
import {
  Button,
  Callout,
  ConfirmModal,
  FailureBanner,
  Label,
  ListTable,
  ListTableHeaderLine,
  Modal,
  RouterLink,
  SelectInput,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { LinkButton } from "@examspark/common-ui/dist/components/buttons/Button";
import {
  IconArchiveLine,
  IconCautionSolid,
  IconNewWindowLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { AwsDateInput } from "@examspark/common-ui/dist/components/inputs/DateInput";
import { LabelLike } from "@examspark/common-ui/dist/components/inputs/Label";
import { useArrayReducer } from "@examspark/common-ui/dist/hooks/useArray";
import { unwrapSetStateAction } from "@examspark/common-ui/dist/utils/react";
import {
  AcademicCalendar,
  Course,
  CourseCalendar,
  NonInstructionDate,
  ResourceVisibilityType,
  Unit,
  UnitPlan,
} from "@examspark/types";

import { ClassroomPlannerPicker } from "../../classroom/planners/ClassroomPlannerCalendar";
import PageHead from "../../common/PageHead";
import PageMenu from "../../common/PageMenu";
import PageTitle from "../../common/PageTitle";
import { NonInstructionDateTable } from "../workspace/NonInstructionDateTable";

const StyledLayout = styled.div`
  display: flex;

  flex-direction: row;
  // flex-wrap: wrap;

  gap: 2rem;
`;

const StyledForm = styled.div`
  flex: 1;
`;

// the 1-parameter arm of `typeof useState<S>` (the actual useState,
//when called with 0 params, forces S to also allow undefined)
type useStateReturnType<S> = [S, React.Dispatch<React.SetStateAction<S>>];
export type CourseCalendarProps = {
  action: "Create" | "Edit";

  course: useStateReturnType<Course | undefined>;
  calendar: useStateReturnType<AcademicCalendar | undefined>;
  name: useStateReturnType<string | undefined>;
  holidays: ReturnType<
    typeof useArrayReducer<NonInstructionDate & { id: string }>
  >;
  plan: useStateReturnType<UnitPlan[] | undefined>;
  visibility: useStateReturnType<ResourceVisibilityType | null>;

  onDelete?: () => void;
  onArchive?: () => void;
  onSave: () => void;
};

const MemoizedCalendarPreview = React.memo(ClassroomPlannerPicker);

export const CourseCalendarForm = ({
  action,
  course: [course, setCourse],
  calendar: [calendar, setCalendar],
  name: [name, setName],
  holidays,
  plan: [plan, setPlan],
  visibility: [visibility, setVisibility],
  onDelete,
  onArchive,
  onSave,
}: CourseCalendarProps) => {
  const [context] = useAppContext();

  // subject is used just in the UI to narrow down courses
  const [subject, setSubject] = useState<string>();

  const courseQuery = useQueryGetCoursesBySubject(subject);
  const academicCalendarQuery = useQueryListAcademicCalendars(
    context.workspaceUser?.workspace?.id,
  );
  const singleCourseQuery = useQueryGetCourse(course?.id);
  const courseUnitsQuery = useQueryGetCourseUnits(course?.id);

  // This, together with the memoized preview, net a huge performance win
  // while typing holiday names (which was laggy)
  const [debouncedCalendarPreview, setDebouncedCalendarPreview] = useState<
    | Pick<
        CourseCalendar,
        "calendar" | "course" | "units" | "additionalNonInstructionDates"
      >
    | undefined
  >();
  useDebounce(
    () => {
      setDebouncedCalendarPreview({
        calendar,
        course,
        units: plan,
        additionalNonInstructionDates: holidays.values,
      });
    },
    250,
    [calendar, course, plan, holidays.values],
  );

  // we need the tags from unitIds but the filtering from the query
  const courseUnits = singleCourseQuery.data?.unitIds?.filter(({ id }) =>
    courseUnitsQuery.data?.find((unit) => unit.id === id),
  );

  const fullCalendar: AcademicCalendar | undefined = calendar
    ? {
        ...calendar,
        nonInstructionDates: [
          ...(calendar.nonInstructionDates ?? []),
          ...holidays.values,
        ],
      }
    : undefined;

  const hasOverlappingUnitPlans =
    plan?.some((unitPlan, i) => {
      if (!unitPlan.dates) return false;

      const unitStart = unitPlan.dates.at(0) ?? "";
      const unitEnd = unitPlan.dates.at(-1) ?? "";

      return plan?.some((otherUnitPlan, j) => {
        if (i === j) return false;
        if (!otherUnitPlan.dates) return false;

        const otherStart = otherUnitPlan.dates.at(0) ?? "";
        const otherEnd = otherUnitPlan.dates.at(-1) ?? "";

        return (
          (unitStart <= otherEnd && unitStart >= otherStart) ||
          (unitEnd <= otherEnd && unitEnd >= otherStart)
        );
      });
    }) ?? false;

  const canSave = !hasOverlappingUnitPlans;

  return (
    <StyledLayout>
      <StyledForm>
        <VSpace size="3rem">
          <PageHead>
            <Breadcrumbs
              items={[
                Breadcrumbs.Dashboard(),
                <RouterLink key="workspace" to={"/workspace"}>
                  Workspace
                </RouterLink>,
                <RouterLink key="calendars" to={"/calendar"}>
                  Calendars
                </RouterLink>,
                <RouterLink key="course-calendars" to={"/calendar/course"}>
                  Course Calendars
                </RouterLink>,
              ]}
            />
            <Toolbar>
              <PageTitle>{action} Course Calendar</PageTitle>
              <Toolbar.Right>
                <PageMenu
                  disabled={false}
                  buttons={[
                    onArchive && {
                      title: "Archive",
                      icon: <IconArchiveLine />,
                      onClick: onArchive,
                    },
                    onDelete && {
                      title: "Delete",
                      icon: <IconTrashLine />,
                      onClick: onDelete,
                    },
                  ]}
                />
              </Toolbar.Right>
            </Toolbar>
          </PageHead>
          <VSpace size="2rem">
            {/* these fields are read-only during edit */}
            {action === "Create" && (
              <>
                <Label label="Academic Calendar">
                  <SelectInput
                    disabled={!academicCalendarQuery.isFetched}
                    value={calendar?.id}
                    options={academicCalendarQuery.data?.map((calendar) => ({
                      label: calendar.name ?? "(Untitled Calendar)",
                      value: calendar.id,
                      calendar,
                    }))}
                    onChange={(option) => setCalendar(option?.calendar)}
                  />
                </Label>
                <Label label="Subject">
                  <SelectInput
                    value={subject}
                    options={Object.entries(subjectMeta)
                      .filter(([key]) => key !== "CTE")
                      .map(([key, { label }]) => ({
                        label,
                        value: key,
                      }))}
                    onChange={(option) => setSubject(option?.value)}
                  />
                </Label>
                <Label label="Course">
                  <SelectInput
                    disabled={!courseQuery.isFetched}
                    value={course?.id}
                    options={courseQuery.data?.map((course) => ({
                      label: course.name ?? "",
                      value: course.id,
                      course,
                    }))}
                    onChange={(option) => setCourse(option?.course)}
                  />
                </Label>
                <Toolbar>
                  <Button
                    title="Calculate Course Calendar"
                    color="primary"
                    disabled={!courseUnitsQuery.isFetched || !calendar}
                    onClick={async () => {
                      // warn the user that this will reset their progress if they already have a plan
                      if (plan) {
                        const should = await ConfirmModal.show({
                          children: (
                            <Modal.PaddedBody>
                              This will reset any changes you have made. Are you
                              sure?
                            </Modal.PaddedBody>
                          ),
                        });

                        if (!should) return;
                      }

                      if (!calendar) return;
                      if (!courseUnits) return;

                      const coursePlan = coursePlanner({
                        calendar,
                        unitIds: courseUnits,
                      });

                      if (!coursePlan?.units) return;

                      setPlan(coursePlan.units);

                      setName(`${calendar.name} - ${course?.name}`);
                    }}
                  >
                    Calculate Course Calendar
                  </Button>
                </Toolbar>
              </>
            )}
            {plan && fullCalendar && (
              <>
                <VSpace>
                  <Label label="Calendar Name">
                    <TextInput
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Label>

                  {/* these would be repeated on the create screen */}
                  {action === "Edit" && (
                    <>
                      <VSpace size="0.5rem">
                        <LabelLike label="Based on Academic Calendar">
                          <Toolbar>
                            <Toolbar.Flex>
                              <TextInput
                                disabled
                                style={{ width: "100%" }}
                                value={calendar?.name ?? "(Untitled Calendar)"}
                              />
                            </Toolbar.Flex>
                            <LinkButton
                              to={`/calendar/workspace/${calendar?.id}`}
                              variant="outlined"
                              disabled={!!calendar?.isDeleted}
                            >
                              <IconNewWindowLine width="16" />
                            </LinkButton>
                          </Toolbar>
                        </LabelLike>
                        {!!calendar?.isDeleted && (
                          <Callout
                            icon={<IconCautionSolid fill="orange" width="16" />}
                          >
                            This acadmic calendar has been deleted or archived.
                          </Callout>
                        )}
                      </VSpace>

                      <LabelLike label="For Course">
                        <Toolbar>
                          <Toolbar.Flex>
                            <TextInput
                              disabled
                              value={course?.name ?? ""}
                              style={{ width: "100%" }}
                            />
                          </Toolbar.Flex>
                          <LinkButton
                            to={`/course/${course?.id}`}
                            variant="outlined"
                          >
                            <IconNewWindowLine width="16" />
                          </LinkButton>
                        </Toolbar>
                      </LabelLike>
                    </>
                  )}

                  {/* 
                I am purposefully leaving out Private for now until we have a
                Creator field or something similar.
              */}
                  <Label label="Visibility">
                    <SelectInput.Simple
                      options={[
                        {
                          value: ResourceVisibilityType.Workspace,
                          label: "Your Workspace",
                        },
                        {
                          value: ResourceVisibilityType.WorkspaceShareable,
                          label: "Your Workspace + Workspaces You Share With",
                        },
                      ]}
                      value={visibility}
                      onChange={(value) => {
                        // silly type check
                        if (
                          value === ResourceVisibilityType.WorkspaceShareable ||
                          value === ResourceVisibilityType.Workspace
                        ) {
                          setVisibility(value);
                        }
                      }}
                    />
                  </Label>
                </VSpace>

                <NonInstructionDateTable
                  holidays={[
                    ...(calendar?.nonInstructionDates?.map((nid) => ({
                      ...nid,
                      id: shortid(),
                      isDisabled: true,
                    })) ?? []),
                    ...holidays.values,
                  ]}
                  onAddHoliday={holidays.onCreate}
                  onRemoveHoliday={holidays.onDelete}
                  onHolidayChange={holidays.onUpdate}
                />

                {course && (
                  <UnitTable
                    course={course}
                    calendar={fullCalendar}
                    unitPlans={plan}
                    onUnitPlanChange={(id, unit) => {
                      setPlan((plan) =>
                        plan?.map((u) => (u.id === id ? { ...u, ...unit } : u)),
                      );
                    }}
                  />
                )}

                {hasOverlappingUnitPlans && (
                  <FailureBanner>
                    Unit / Module dates cannot overlap.
                  </FailureBanner>
                )}

                <Toolbar>
                  <LinkButton to="/calendar/course">Cancel</LinkButton>
                  <Button
                    title={`${
                      action === "Create" ? "Create" : "Save"
                    } Course Calendar`}
                    color="primary"
                    onClick={(e) => {
                      if (e.altKey) {
                        console.log({
                          ...calendar,
                          units: plan,
                        });
                      } else {
                        onSave();
                      }
                    }}
                    disabled={canSave ? undefined : true}
                  >
                    {action === "Create" ? "Create Calendar" : "Save Calendar"}
                  </Button>
                </Toolbar>
              </>
            )}
          </VSpace>
        </VSpace>
      </StyledForm>
      <div style={{ minWidth: "500px" }}>
        {debouncedCalendarPreview && (
          <MemoizedCalendarPreview courseCalendar={debouncedCalendarPreview} />
        )}
      </div>
    </StyledLayout>
  );
};

const StyledUnitName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledUnitRow = styled.tr`
  td {
    padding-block: 1rem;
  }
`;

const StyledDuration = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const UnitTable = ({
  course,
  calendar,
  unitPlans,
  onUnitPlanChange,
}: {
  course: Course;
  calendar: AcademicCalendar;
  unitPlans: UnitPlan[];

  onUnitPlanChange: (id: string, unitPlan: Partial<UnitPlan>) => void;
}) => {
  const courseWithUnitsQuery = useQueryGetCourseUnits(course?.id);

  const unitLookup = courseWithUnitsQuery.data?.reduce(
    (acc, unit) => ({ ...acc, [unit.id]: unit }),
    {} as Record<string, Unit>,
  );

  const allowableDates = instructionDatesBetween(
    calendar,
    calendar.startAt,
    calendar.endAt,
  );

  const recalculateUnitPlan = (
    unitPlan: UnitPlan,
    newStartDate: string,
    newEndDate: string,
  ) => {
    if (!unitPlan.id) return;

    const dates = instructionDatesBetween(calendar, newStartDate, newEndDate);
    onUnitPlanChange(unitPlan.id, {
      dates,
    });
  };

  return (
    <ListTable>
      <caption>Units / Modules</caption>

      {unitPlans?.length > 0 && (
        <thead>
          <tr>
            <th style={{ minWidth: "15ch" }}>Name</th>
            <th style={{ width: "10rem" }}>Duration</th>
            <th style={{ width: "12rem" }}>Start</th>
            <th style={{ width: "12rem" }}>End</th>
          </tr>

          <ListTableHeaderLine />
        </thead>
      )}
      <tbody>
        {unitPlans?.map((unitPlan) => {
          if (!unitPlan.id) return null;

          const unit = unitLookup?.[unitPlan.id];
          const currentStartDate = unitPlan.dates?.at(0);
          const currentEndDate = unitPlan.dates?.at(-1);

          if (!unit || !currentStartDate || !currentEndDate) return null;

          return (
            <StyledUnitRow key={unitPlan.id}>
              <td>
                <StyledUnitName>
                  <span>{unit?.name}</span>
                </StyledUnitName>
              </td>

              <td>
                <StyledDuration>
                  {unitPlan.dates?.length ?? 0} days
                </StyledDuration>
              </td>

              <td>
                <AwsDateInput
                  date={currentStartDate}
                  onDateChange={(startAt) => {
                    recalculateUnitPlan(
                      unitPlan,
                      unwrapSetStateAction(currentStartDate, startAt),
                      currentEndDate,
                    );
                  }}
                  minDate={calendar.startAt}
                  maxDate={currentEndDate}
                  tileDisabled={({ date }) => {
                    return !allowableDates.includes(toAWSDate(date));
                  }}
                />
              </td>
              <td>
                <AwsDateInput
                  date={currentEndDate}
                  onDateChange={(endAt) => {
                    recalculateUnitPlan(
                      unitPlan,
                      currentStartDate,
                      unwrapSetStateAction(currentEndDate, endAt),
                    );
                  }}
                  minDate={currentStartDate}
                  maxDate={calendar.endAt}
                  tileDisabled={({ date }) => {
                    return !allowableDates.includes(toAWSDate(date));
                  }}
                />
              </td>
            </StyledUnitRow>
          );
        })}
      </tbody>
    </ListTable>
  );
};
